import {
  MERGE_TAG_PATTERN,
  parseTaskTemplateDueOffset,
  type TaskPriority,
  type TaskTemplateFieldWire,
} from "@quovy/types";

import type { TaskIdentitySummary } from "./task-detail-view";

/**
 * The runtime task-template model shared by both UI surfaces: the create-task
 * surface (template selection + instantiation, this file's `TaskTemplate`)
 * and the template editor (`TaskTemplateDraft` in `task-template-form.tsx`,
 * which holds the same fields/defaults but keeps the two template bodies as
 * chip documents while editing). Field and default definitions are the wire
 * shapes from `@quovy/types` — the only UI-side deviation is `defaults`,
 * whose assignee is a resolved {@link TaskIdentitySummary} rather than a
 * public-id reference.
 */

export type { TaskTemplateFieldType } from "@quovy/types";

/** One merge-tag field definition — the wire shape, used as-is in the UI. */
export type TaskTemplateField = TaskTemplateFieldWire;

/** Non-parameterized defaults applied to the created task. */
export interface TemplateTaskDefaults {
  assignee?: TaskIdentitySummary;
  /** Default task priority, applied to the created task at submit. */
  priority?: TaskPriority;
  /** Relative due date label, e.g. "+1 business day". */
  dueOffset?: string;
}

export interface TaskTemplate {
  publicId: string;
  /** Library name (what the template is for), distinct from the task title. */
  name: string;
  description: string;
  /** Ordered field definitions; the instantiate form renders top-to-bottom. */
  fields: TaskTemplateField[];
  /** Task title with `{{merge_tags}}`. */
  titleTemplate: string;
  /** Task description with `{{merge_tags}}`. */
  descriptionTemplate: string;
  defaults: TemplateTaskDefaults;
}

/** A rendered chunk of a template string, for highlighted previews. */
export type TemplateSegment =
  | { kind: "text"; text: string }
  | { kind: "value"; text: string; key: string }
  | { kind: "tag"; text: string; key: string };

/**
 * Split a template string into literal text, substituted values, and
 * still-unresolved `{{tags}}` so previews can highlight substitutions live.
 */
export function renderTemplateSegments(
  template: string,
  values: Record<string, string>,
): TemplateSegment[] {
  const segments: TemplateSegment[] = [];
  let lastIndex = 0;
  for (const match of template.matchAll(MERGE_TAG_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      segments.push({ kind: "text", text: template.slice(lastIndex, index) });
    }
    const key = match[1]!;
    const value = values[key]?.trim();
    if (value) {
      segments.push({ kind: "value", text: value, key });
    } else {
      segments.push({ kind: "tag", text: match[0]!, key });
    }
    lastIndex = index + match[0]!.length;
  }
  if (lastIndex < template.length) {
    segments.push({ kind: "text", text: template.slice(lastIndex) });
  }
  return segments;
}

/**
 * Substitute `{{tags}}` with provided values. Tags without a non-empty value
 * are left verbatim — the "edit as blank task" escape hatch keeps them visible
 * so nothing is silently dropped.
 */
export function renderTemplateString(template: string, values: Record<string, string>): string {
  return renderTemplateSegments(template, values)
    .map((segment) => segment.text)
    .join("");
}

/** Initial form values: each field's default, or empty. */
export function initialTemplateValues(template: TaskTemplate): Record<string, string> {
  return Object.fromEntries(template.fields.map((field) => [field.key, field.default ?? ""]));
}

/**
 * Resolve a relative due-offset label ("+1 business day", "+3 days") to a
 * concrete epoch-ms date. This is the load-bearing, client-side resolution:
 * it runs when a template is applied (not at submit), and its result is what
 * `POST /v1/tasks` receives as the concrete due date. "Business day" is a
 * naive weekend skip (no holiday calendar). Returns null for unparseable
 * offsets. Grammar and the ≤9999-day bound come from
 * {@link parseTaskTemplateDueOffset} — the same parser the wire schema
 * validates with, so a stored offset can never drive the O(n) day-walk into
 * a UI-freezing loop.
 */
export function resolveDueOffset(offset: string, from: Date = new Date()): number | null {
  const parsed = parseTaskTemplateDueOffset(offset);
  if (!parsed) return null;
  const date = new Date(from);
  date.setHours(0, 0, 0, 0);
  let remaining = parsed.days;
  while (remaining > 0) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (!parsed.business || (day !== 0 && day !== 6)) remaining -= 1;
  }
  return date.getTime();
}

/** "1 field" / "3 fields" / "no fields" — used as the menu-item hint. */
export function templateFieldCountLabel(template: TaskTemplate): string {
  const count = template.fields.length;
  if (count === 0) return "no fields";
  return count === 1 ? "1 field" : `${count} fields`;
}
